import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface MetaConfig {
    appId: string;
    configurationId: string;
    /** URL pronta do diálogo OAuth, já com redirect_uri e state. */
    urlAutorizacao: string;
}

/** Endereço para onde a Meta devolve o navegador. Registrado no App Dashboard. */
export const TRAFEGO_PAGO_REDIRECT_PATH = '/integracoes/meta-ads/callback';
export const TRAFEGO_PAGO_AUTH_MESSAGE = 'lemeai:meta-ads-auth';

export const montarTrafegoPagoRedirectUri = () =>
    `${window.location.origin}${TRAFEGO_PAGO_REDIRECT_PATH}`;

export type StatusSincronizacao = 'Ativa' | 'Pausada' | 'ErroAutenticacao' | 'ErroPermissao';

export interface ContaAnuncio {
    contaAnuncioId: number;
    contaExternaId: string;      // "act_123456789"
    nome: string;
    moeda: string;               // "BRL"
    fusoHorario: string;         // "America/Sao_Paulo"
    statusSincronizacao: string; // StatusSincronizacao
    ultimaSincronizacaoEm: string | null;
    ultimoErro: string | null;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

/** "Anuncio" (pago), "Publicacao" (post com botão de WhatsApp) ou "Story" (resposta a story do Instagram). */
export type FonteTipoAtribuicao = 'Anuncio' | 'Publicacao' | 'Story';

/**
 * De qual anúncio veio uma conversa. **Já desce** no payload da listagem do chat
 * (`ConversaPorVendedorDTO.atribuicaoAnuncio`) e no do lead — não há chamada nova
 * para mostrar a origem no atendimento.
 *
 * `enriquecido: false` quer dizer que o nome da campanha ainda não foi resolvido
 * pela Meta: o id existe, o nome não. Tratar como "sem nome ainda", nunca como
 * vazio.
 */
export interface AtribuicaoAnuncio {
    atribuicaoAnuncioId: number;
    origem: string;
    /** O que gerou o contato. Ausente (backend antigo) vale "Anuncio". Publicação e story não têm anuncioId. */
    fonteTipo?: FonteTipoAtribuicao;
    anuncioId: string | null;
    anuncioNome: string | null;
    conjuntoAnuncioNome: string | null;
    campanhaAnuncioNome: string | null;
    /** Objetivo da campanha na Meta (OUTCOME_SALES...). Nulo até o enriquecimento. */
    campanhaAnuncioObjetivo?: string | null;
    /** effective_status do anúncio (ACTIVE, PAUSED...), reconferido a cada 24h. */
    anuncioStatus?: string | null;
    /** Devolução da venda à Meta (diagnóstico; não aparece para o vendedor). */
    conversaoMeta?: string;
    anuncioTitulo: string | null;
    anuncioCorpo: string | null;
    anuncioMidiaUrl: string | null;
    anuncioUrlOrigem: string | null;
    /** Onde o anúncio rodou: "instagram" | "facebook". Nulo quando a Meta não diz. */
    anuncioRede?: string | null;
    /** "video" | "image" — decide o rótulo do botão. */
    anuncioTipoMidia?: string | null;
    /** Capa guardada pelo Brik (api/Media). O anuncioMidiaUrl é de CDN e vence em ~4 dias. */
    anuncioCapaCaminho?: string | null;
    enriquecido: boolean;
    dataCriacao: string;
}

/** "api.whatsapp.com", "https://wa.me/55…": endereço de site, não título. */
const pareceEndereco = (t: string) => /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(t);

/**
 * O título que vale como nome. Anúncio sem título escrito chega com o endereço para onde
 * leva no lugar dele (medido em 17/10/2026, Loja do Menino: "api.whatsapp.com") — e isso
 * não é nome de nada.
 */
const tituloDoAnuncio = (a: AtribuicaoAnuncio): string | null => {
    const t = a.anuncioTitulo?.trim();
    return t && !pareceEndereco(t) ? t : null;
};

const linhasDe = (t: string) => t.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

/**
 * Como a origem é escrita na tela. "Campanha" sozinho é ambíguo no sistema — já
 * quer dizer disparo de WhatsApp —, então o nome do ANÚNCIO é o rótulo principal
 * e a campanha vem como apoio.
 */
export const ehAnuncioPago = (a: AtribuicaoAnuncio): boolean =>
    !a.fonteTipo || a.fonteTipo === 'Anuncio';

/** "Anúncio", "Publicação" ou "Story": a palavra que abre a origem nas listas e nos tooltips. */
export const palavraDaOrigem = (a: AtribuicaoAnuncio): string =>
    a.fonteTipo === 'Publicacao' ? 'Publicação' : a.fonteTipo === 'Story' ? 'Story' : 'Anúncio';

/** A frase curta da origem, para tooltip e leitor de tela: "Veio do anúncio", "Veio de uma publicação"... */
export const fraseDaOrigem = (a: AtribuicaoAnuncio): string =>
    a.fonteTipo === 'Publicacao' ? 'Veio de uma publicação'
        : a.fonteTipo === 'Story' ? 'Veio de um story'
            : 'Veio do anúncio';

export const nomeDoAnuncio = (a: AtribuicaoAnuncio) =>
    a.anuncioNome?.trim()
    /* O título vem junto da mensagem, sem conta de anúncios conectada. Sem ele, a
       empresa que só anuncia (e não liga a conta) via "Sem nome ainda" para sempre:
       o nome da campanha só chega pela conta. */
    || tituloDoAnuncio(a)
    /* Sem título de verdade, a 1ª linha do texto é o que a pessoa leu no anúncio. */
    || linhasDe(a.anuncioCorpo ?? '')[0]
    /* Publicação e story não passam por enriquecimento: "Sem nome ainda" nunca deixaria de valer. */
    || (a.fonteTipo === 'Publicacao' ? 'Publicação do perfil'
        : a.fonteTipo === 'Story' ? 'Resposta a um story'
            : a.enriquecido ? 'Anúncio sem nome' : 'Sem nome ainda');

/**
 * O texto que acompanha o nome no Chat e na ficha: o título (quando o nome veio da Meta) e o
 * corpo, sem repetir o que já virou nome — se o nome saiu da 1ª linha do corpo, sobra o resto.
 */
export const textoDoAnuncio = (a: AtribuicaoAnuncio): string => {
    const nome = nomeDoAnuncio(a);
    const titulo = tituloDoAnuncio(a);
    const bruto = a.anuncioCorpo?.trim() ?? '';
    /* As quebras de linha do texto ficam: o Chat e a ficha mostram o corpo como a pessoa escreveu. */
    const corpo = linhasDe(bruto)[0] === nome ? bruto.replace(/^[^\r\n]*/, '').trim() : bruto;
    return [titulo && titulo !== nome ? titulo : null, corpo || null].filter(Boolean).join(' ');
};

/** A capa guardada pelo Brik, pronta para o <img>. Nula até o robô baixar (≤ 2 min) ou sem capa. */
export const capaDoAnuncio = (a: AtribuicaoAnuncio): string | null =>
    a.anuncioCapaCaminho?.trim() ? `${API_URL}/api/Media/${a.anuncioCapaCaminho.trim()}` : null;

/** "Anúncio no Instagram" — onde o anúncio rodou, não o canal da conversa (que aparece ao lado). */
export const rotuloRedeDoAnuncio = (a: AtribuicaoAnuncio): string => {
    if (a.fonteTipo === 'Story') return 'Resposta a um story';
    if (a.fonteTipo === 'Publicacao') {
        return a.anuncioRede === 'instagram' ? 'Publicação no Instagram'
            : a.anuncioRede === 'facebook' ? 'Publicação no Facebook'
                : 'Veio de uma publicação';
    }
    return a.anuncioRede === 'instagram' ? 'Anúncio no Instagram'
        : a.anuncioRede === 'facebook' ? 'Anúncio no Facebook'
            : 'Veio do anúncio';
};

/**
 * O link que abre o anúncio para quem atende, com o nome do que vai abrir: vídeo do
 * Instagram é reels. O Instagram Direct não manda link, só a imagem — e o endereço de
 * CDN expira em dias, por isso a capa guardada vem antes dele.
 */
export const linkDoAnuncio = (a: AtribuicaoAnuncio): { url: string; rotulo: string } | null => {
    if (a.anuncioUrlOrigem?.trim()) {
        const rotulo = a.anuncioTipoMidia === 'video'
            ? (a.anuncioRede === 'instagram' ? 'Ver o reels' : 'Ver o vídeo')
            : a.fonteTipo === 'Publicacao' ? 'Ver a publicação' : 'Ver o anúncio';
        return { url: a.anuncioUrlOrigem.trim(), rotulo };
    }
    /* O story não manda link: só a mídia, que é de CDN e vence em horas — a capa guardada vem antes. */
    const rotuloMidia = a.fonteTipo === 'Story' ? 'Ver o story' : 'Ver a imagem';
    const capa = capaDoAnuncio(a);
    if (capa) return { url: capa, rotulo: rotuloMidia };
    if (a.anuncioMidiaUrl?.trim()) return { url: a.anuncioMidiaUrl.trim(), rotulo: rotuloMidia };
    return null;
};

export const campanhaDoAnuncio = (a: AtribuicaoAnuncio) =>
    a.campanhaAnuncioNome?.trim() || null;

/* Os objetivos de hoje (OUTCOME_*) e os antigos, que campanhas mais velhas ainda devolvem. */
const OBJETIVOS: Record<string, string> = {
    OUTCOME_SALES: 'Vendas',
    OUTCOME_LEADS: 'Cadastros',
    OUTCOME_ENGAGEMENT: 'Engajamento',
    OUTCOME_TRAFFIC: 'Tráfego',
    OUTCOME_AWARENESS: 'Reconhecimento',
    OUTCOME_APP_PROMOTION: 'Promoção de app',
    MESSAGES: 'Mensagens',
    CONVERSIONS: 'Conversões',
    LEAD_GENERATION: 'Cadastros',
    LINK_CLICKS: 'Tráfego',
    POST_ENGAGEMENT: 'Engajamento',
};

/** "Vendas", "Cadastros"... — a intenção da campanha. Código desconhecido não aparece: seria jargão da Meta. */
export const objetivoDoAnuncio = (a: AtribuicaoAnuncio): string | null =>
    OBJETIVOS[a.campanhaAnuncioObjetivo?.trim().toUpperCase() ?? ''] ?? null;

/**
 * O status do anúncio, só quando NÃO está rodando — "ativo" é o normal e não merece selo.
 * Pausado no conjunto ou na campanha também é pausado para quem atende.
 */
export const statusDoAnuncio = (a: AtribuicaoAnuncio): { rotulo: string; tom: 'warning' | 'neutral' | 'danger' } | null => {
    switch (a.anuncioStatus?.trim().toUpperCase()) {
        case 'PAUSED':
        case 'ADSET_PAUSED':
        case 'CAMPAIGN_PAUSED':
            return { rotulo: 'Pausado', tom: 'warning' };
        case 'ARCHIVED':
        case 'DELETED':
            return { rotulo: 'Encerrado', tom: 'neutral' };
        case 'DISAPPROVED':
            return { rotulo: 'Reprovado', tom: 'danger' };
        case 'WITH_ISSUES':
            return { rotulo: 'Com problemas', tom: 'danger' };
        default:
            return null;
    }
};

/** "campanha" (padrão), "conjunto" ou "anuncio" — o grão do agrupamento. */
export type AgrupamentoAnuncio = 'campanha' | 'conjunto' | 'anuncio';

export interface FiltroDesempenho {
    /** AAAA-MM-DD, no fuso da conta de anúncios. */
    de: string;
    ate: string;
    agruparPor?: AgrupamentoAnuncio;
    contaAnuncioId?: number | null;
}

/**
 * Uma linha do relatório. Os três derivados vêm `null` — e não zero — quando o
 * denominador é zero: sem lead não há CPL, sem venda não há custo por venda, sem
 * gasto não há ROAS. Mostrar zero nesses casos seria afirmar algo que não se mediu.
 */
export interface DesempenhoAnuncio {
    identificador: string | null;
    nome: string | null;
    gasto: number;
    impressoes: number;
    cliques: number;
    leads: number;
    vendas: number;
    receita: number;
    custoPorLead: number | null;
    custoPorVenda: number | null;
    roas: number | null;
    /** Quantos conjuntos e anúncios a linha soma. No grão do anúncio os dois são 1. */
    conjuntos: number;
    anuncios: number;
    moeda: string;
}

/** O criativo de um anúncio: o que o cliente viu antes de mandar mensagem. */
export interface CriativoAnuncio {
    titulo: string | null;
    corpo: string | null;
    midiaUrl: string | null;
    urlOrigem: string | null;
}

export interface PainelDoAnuncio {
    /** null quando o anúncio não foi enriquecido ou não trouxe criativo. */
    criativo: CriativoAnuncio | null;
    leads: LeadDoAnuncio[];
    /** true quando há mais leads do que os devolvidos. */
    truncado: boolean;
}

/** Uma conversa que nasceu de um anúncio — o que enche o painel. */
export interface LeadDoAnuncio {
    idConversa: number;
    nomeContato: string;
    numeroWhatsapp: string;
    statusId: number;
    statusDescricao: string;
    valor: number;
    /** Ganho é o STATUS, não "tem valor": valor existe em venda perdida também. */
    ganhou: boolean;
    nomeResponsavel: string | null;
    entrouEm: string;
}

/**
 * Um dia da série do gráfico. Vem SEMPRE completa — dia sem movimento com zeros,
 * não ausente: buraco faria a linha pular o fim de semana e desenhar uma queda
 * que não existiu.
 */
export interface SerieDiariaAnuncio {
    dia: string;          // AAAA-MM-DD
    gasto: number;
    leads: number;
    vendas: number;
    receita: number;
    /** Vazio quando o período mistura moedas — aí a tela não desenha o gráfico. */
    moeda: string;
}

export interface ResumoAnuncios {
    gastoTotal: number;
    leadsTotal: number;
    vendasTotal: number;
    receitaTotal: number;
    custoPorLead: number | null;
    custoPorVenda: number | null;
    roas: number | null;
    moeda: string;
    /** Contas em moedas diferentes NÃO são somadas: os totais vêm em `resumoPorMoeda`. */
    multiMoeda: boolean;
    resumoPorMoeda?: ResumoAnuncios[] | null;
    /**
     * Leads de anúncios que NÃO tiveram gasto no período. Ficam de fora dos
     * totais acima de propósito — não há gasto para dividir —, mas existem: sem
     * mostrá-los, o número da tela fica menor que o que o CRM recebeu e a
     * diferença some sem explicação.
     */
    leadsSemCusto: number;
    vendasSemCusto: number;
    receitaSemCusto: number;
    anunciosSemCusto: number;
}

export const TrafegoPagoService = {
    /** appId + configurationId da configuração de tráfego pago (NÃO é a do WhatsApp). */
    getMetaConfig: async (state: string): Promise<{ sucesso: boolean; mensagem?: string; dados?: MetaConfig }> => {
        try {
            const query = new URLSearchParams({ redirectUri: montarTrafegoPagoRedirectUri(), state });
            const response = await apiFetch(`${API_URL}/api/trafegopago/ConfiguracaoMeta?${query}`);
            if (response.ok) {
                return await response.json();
            }
            return { sucesso: false, mensagem: 'Configuração de tráfego pago indisponível.' };
        } catch (error) {
            console.error('Erro ao obter configuração de tráfego pago:', error);
            return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
        }
    },

    /** Envia o authorization code. O token nunca passa pelo front. */
    conectarConta: async (code: string): Promise<{ sucesso: boolean; mensagem?: string; dados?: ContaAnuncio[] }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/trafegopago/ConectarConta`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, redirectUri: montarTrafegoPagoRedirectUri() })
            });

            if (response.ok) {
                return await response.json();
            }

            const erro = await response.json();
            return { sucesso: false, mensagem: erro.mensagem || 'Erro ao conectar conta de anúncios.' };
        } catch (error) {
            console.error('Erro ao conectar conta de anúncios:', error);
            return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
        }
    },

    buscarContas: async (): Promise<ContaAnuncio[]> => {
        try {
            const response = await apiFetch(`${API_URL}/api/trafegopago/BuscarContas`);
            if (response.ok) {
                const json: ApiResponse<ContaAnuncio[]> = await response.json();
                return json.dados ?? [];
            }
        } catch (error) {
            console.error('Erro ao buscar contas de anúncio:', error);
        }
        return [];
    },

    removerConta: async (id: number): Promise<{ sucesso: boolean; mensagem?: string }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/trafegopago/RemoverConta/${id}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                return await response.json();
            }

            const erro = await response.json();
            return { sucesso: false, mensagem: erro.mensagem || 'Erro ao remover conta.' };
        } catch (error) {
            console.error('Erro ao remover conta de anúncio:', error);
            return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
        }
    },

    /**
     * Uma linha por campanha, conjunto ou anúncio, já com CPL, custo por venda e
     * ROAS calculados no servidor. Exige a permissão `anuncios` (AnunciosPolicy):
     * 403 aqui significa perfil sem acesso ao custo, não erro de rede.
     */
    desempenho: async (filtro: FiltroDesempenho): Promise<DesempenhoAnuncio[]> => {
        const response = await apiFetch(`${API_URL}/api/trafegopago/Desempenho`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                de: filtro.de,
                ate: filtro.ate,
                agruparPor: filtro.agruparPor ?? 'campanha',
                contaAnuncioId: filtro.contaAnuncioId ?? null
            })
        });

        if (!response.ok) {
            throw new Error(response.status === 403
                ? 'Seu perfil não tem acesso ao desempenho de anúncios.'
                : 'Não foi possível carregar o desempenho dos anúncios.');
        }

        const json: ApiResponse<DesempenhoAnuncio[]> = await response.json();
        return json.dados ?? [];
    },

    /** Os totais do mesmo período. Ver `multiMoeda` antes de somar qualquer coisa. */
    resumo: async (filtro: FiltroDesempenho): Promise<ResumoAnuncios | null> => {
        const response = await apiFetch(`${API_URL}/api/trafegopago/Resumo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                de: filtro.de,
                ate: filtro.ate,
                agruparPor: filtro.agruparPor ?? 'campanha',
                contaAnuncioId: filtro.contaAnuncioId ?? null
            })
        });

        if (!response.ok) {
            throw new Error(response.status === 403
                ? 'Seu perfil não tem acesso ao desempenho de anúncios.'
                : 'Não foi possível carregar o resumo dos anúncios.');
        }

        const json: ApiResponse<ResumoAnuncios> = await response.json();
        return json.dados ?? null;
    },

    /** Gasto, leads, vendas e receita dia a dia — a série do gráfico. */
    porDia: async (filtro: FiltroDesempenho): Promise<SerieDiariaAnuncio[]> => {
        const response = await apiFetch(`${API_URL}/api/trafegopago/PorDia`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                de: filtro.de,
                ate: filtro.ate,
                agruparPor: 'anuncio',
                contaAnuncioId: filtro.contaAnuncioId ?? null
            })
        });
        if (!response.ok) throw new Error('Não foi possível carregar a série diária.');
        const json: ApiResponse<SerieDiariaAnuncio[]> = await response.json();
        return json.dados ?? [];
    },

    /**
     * O painel de um anúncio: o criativo e as conversas que ele gerou, numa
     * chamada só. A lista vem no máximo com 50 — é amostra para navegar até o
     * atendimento, não relatório; o total fica na linha da lista.
     */
    painelDoAnuncio: async (anuncioId: string, de: string, ate: string): Promise<PainelDoAnuncio> => {
        const query = new URLSearchParams({ anuncioId, de, ate });
        const response = await apiFetch(`${API_URL}/api/trafegopago/LeadsDoAnuncio?${query}`);
        if (!response.ok) throw new Error('Não foi possível carregar os leads deste anúncio.');
        const json: ApiResponse<PainelDoAnuncio> = await response.json();
        return json.dados ?? { criativo: null, leads: [], truncado: false };
    }
};
